import * as React from 'react';
import * as _ from 'lodash';
import { connect, ConnectedProps } from 'react-redux';
import { IStore } from '../../../../redux/initialState';
import OutsideClickPanel from '../../../components/OutsideClickPanel/OutsideClickPanel';
import PopupButtons from '../../../components/Popup/PopupButtons';
import PopupCancelButton from '../../../components/Popup/PopupCancelButton';
import JsonPanelBody from '../JsonPanel/JsonPanelBody';
import JsonPanel from '../JsonPanel/JsonPanel';
import { useCallback, useEffect, useRef, useState } from 'react';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import { copyTransformRow, getTransformInfo, ICopyTransformRowParam, IGetTransformInfoParam } from '../../../../redux/projects/actions.projects';
import { Environment } from '../../../../const/environment';
import TransformView from '../TransformMode/TransformView/TransformView';
import { IScript } from '../../../../models/IScript';
import { ITransform, ITransformRow } from '../../../../models/ITransform';
import { getTransformFromTransformFile } from '../TransformMode/getTransformFromTransformFile';
import { IProject } from '../../../../models/IProject';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import { transformsStore } from '../../../../mobx/transforms.store';

type InspectTransformPanelOwnProps = {
    project: IProject,
    environment: Environment,
    transformName: string,
    onClose: () => void,
};

const LIMIT = 15;

function InspectTransformPanel({
    project,
    environment,
    transformName,
    getTransformInfo,
    onClose,
    copyTransformRow
}: PropsFromRedux & InspectTransformPanelOwnProps) {


    const [opened, setOpened] = useState(false);
    const [script, setScript] = useState(null);
    const [showMore, setShowMore] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [transformFiles, setTransformFiles] = useState(null);
    const [scrollInTheEnd, setScrollInTheEnd] = useState(false);

    const paramsRef = useRef({ offset: 0, paginationKey: '', final: false });

    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (!opened) {
            transformsStore.resetHistoryTransform();
            setScript(null);
        }
    }, [opened]);

    const requestData = useCallback(_.throttle(() => {
        if (transformName && !paramsRef.current.final) {
            setShowLoader(true);
            setShowMore(false);
            getTransformInfo({
                projectId: project.projectId,
                transformName,
                environment,
                limit: LIMIT,
                paginationKey: paramsRef?.current?.paginationKey,
                offset: paramsRef?.current?.offset,
            }).then(({ transformFiles, receivedPaginationKey }) => {
                if (isMounted.current) {
                    setShowLoader(false);
                    paramsRef.current.offset = paramsRef.current.offset + LIMIT;
                    paramsRef.current.paginationKey = receivedPaginationKey;
                    paramsRef.current.final = transformFiles?.length < LIMIT;
                    setShowMore(transformFiles?.length === LIMIT);
                    setScrollInTheEnd(false);
                    setTransformFiles(prevTransforms => {
                        return [...(prevTransforms || []), ...transformFiles];
                    });
                }
            }).catch(err => {
                console.error(err);
            });
        }
    }, 1000), [environment, getTransformInfo, project.projectId, transformName]);


    const handleScroll = useCallback((inTheEnd) => {
        if (scrollInTheEnd !== inTheEnd) {
            setScrollInTheEnd(inTheEnd);
        }
    }, [scrollInTheEnd]);

    const handleCopy = useCallback((row: ITransformRow) => {
        onClose();
        copyTransformRow({
            row: {
                input: row.input,
                query: row.query,
                result: row.result,
                prompt: '',
            } as ITransformRow, project, transformName, environment
        });
    }, [copyTransformRow, environment, onClose, project, transformName]);

    const handleLoadMore = useCallback((e) => {
        requestData();
    }, [requestData]);


    useEffect(() => {
        setOpened(!!transformName);
        requestData();
    }, [requestData, transformName]);

    useEffect(() => {
        if (transformFiles && transformName) {
            const script = { name: transformName, text: '' } as never as IScript;
            transformsStore.initHistoryTransform(getTransformFromTransformFile(transformFiles))
            setScript(script);
        }
    }, [transformFiles, transformName]);


    const onInspectPopupCloseCb = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const cellPopupEl = document.querySelector('.transform-row-popup');
        if (!cellPopupEl) {
            onClose();
        }
    }, [onClose]);

    const initTransform = transformsStore.historyTransform;

    return <OutsideClickPanel
        onOutsideClick={onInspectPopupCloseCb}
        opened={opened}
        classes={` outside-click-panel inspect-transform-panel-holder ${opened && 'opened'}`}>
        <div className="inspect-transform-panel">
            <i className="icon icon-alan-remove-fat inspect-transform-panel__close-icon" onClick={onClose}></i>
            <JsonPanel className="inspect-transform-panel__json">
                <JsonPanelBody>
                    {transformFiles === null ?
                        <Loader size={LoaderSize.Medium} theme={LoaderTheme.Dark} /> : <>

                            {transformFiles?.length === 0 ?
                                <div className="inspect-transform-panel__no-data">No data for transform&nbsp;<b>{transformName || '-'}</b></div> :
                                <>
                                    {(script && initTransform && transformFiles?.length) &&
                                        <>
                                            <TransformView
                                                transformLabel="History of transform"
                                                previewMode={true}
                                                environment={environment}
                                                script={script}
                                                project={project}
                                                transform={initTransform}
                                                onCopy={handleCopy}
                                                onClose={onClose}
                                                onScrollCb={handleScroll}
                                            />
                                            {showLoader && <Loader size={LoaderSize.Medium} theme={LoaderTheme.Dark} position="absolute" center={true} />}
                                        </>
                                    }
                                </>}
                        </>}
                </JsonPanelBody>
                <PopupButtons>
                    {(showMore && scrollInTheEnd) && <div onClick={handleLoadMore} className="inspect-transform-panel__load-more-btn">Load More</div>}
                </PopupButtons>
            </JsonPanel>
        </div>
    </OutsideClickPanel>;
}

function mapStateToProps({ codeMetaData, projectsInfo }: IStore, ownProps: InspectTransformPanelOwnProps) {
    return {
        isProjectConnected: projectsInfo.currentProject.isProjectConnected,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        getTransformInfo(param: IGetTransformInfoParam): Promise<any> {
            return asyncDispatch(getTransformInfo, dispatch, param);
        },
        copyTransformRow(param: ICopyTransformRowParam) {
            asyncDispatch(copyTransformRow, dispatch, param);
        },
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(InspectTransformPanel);
